import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./advisor-image-form.i18n";
import type { ComponentFormProps } from "./types";

export type AdvisorImageConfig = {
  width: number;
  align: "left" | "center" | "right";
  shape: "square" | "circle";
};

type AdvisorImageFormProps = ComponentFormProps<AdvisorImageConfig>;

export function AdvisorImageForm({ config, onChange }: AdvisorImageFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-muted rounded-md">
        <p className="text-sm text-muted-foreground">{t.description}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="advisor-image-width">{t.width}</Label>
          <Input
            id="advisor-image-width"
            type="number"
            min="10"
            max="100"
            value={config.width || 20}
            onChange={(e) => onChange({ ...config, width: parseInt(e.target.value, 10) || 20 })}
          />
          <p className="text-xs text-muted-foreground">{t.widthHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="advisor-image-align">{t.alignment}</Label>
          <Select
            value={config.align || "center"}
            onValueChange={(value: "left" | "center" | "right") =>
              onChange({ ...config, align: value })
            }
          >
            <SelectTrigger id="advisor-image-align">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">{t.left}</SelectItem>
              <SelectItem value="center">{t.center}</SelectItem>
              <SelectItem value="right">{t.right}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="advisor-image-shape">{t.shape}</Label>
        <Select
          value={config.shape || "circle"}
          onValueChange={(value: "square" | "circle") => onChange({ ...config, shape: value })}
        >
          <SelectTrigger id="advisor-image-shape">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="circle">{t.circle}</SelectItem>
            <SelectItem value="square">{t.square}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.shapeHelp}</p>
      </div>
    </div>
  );
}

export function validateAdvisorImageConfig(_config: AdvisorImageConfig): string | null {
  // No validation needed - the image comes from the advisor's profile
  return null;
}

export function getDefaultAdvisorImageConfig(): AdvisorImageConfig {
  return {
    width: 20,
    align: "center",
    shape: "circle",
  };
}
